import { useClinicalEditors } from './context';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import {
  CheckCircle2,
  Clock3,
  Pencil,
  Upload,
  Send,
  Image as ImageIcon,
  X,
  Info,
  FileText,
  Phone,
  Plus,
  Minus,
  Search,
  Building2,
  Eye,
  Sparkles,
} from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Textarea } from '@rudi/ui/textarea';
import { Label } from '@rudi/ui/label';
import { AdaptiveDialog } from '@rudi/ui/adaptive-dialog';
import { usePublicInvestigator } from './usePublicInvestigator';
import {
  useProfileEditRequest,
  type ProfileEditRequestPayload,
  type ProfileEditRequest,
  type ClinicRefDto,
} from './useProfileEditRequest';
import {
  publishProfileDraft,
  clearProfileDraft,
  subscribeProfileDraft,
  type ProfileDraftPreview,
} from './profileDraftBus';

import type {
  PublicClinicSummary,
  PublicInvestigatorDetail,
} from '@rudi/shared/types/clinical-trials';
import type {
  PiSuggestionResponse,
  PiSuggestionSurface,
  PiSuggestionSurfaceStatus,
} from '@rudi/shared/types/clinical-trials';
import { rankSearchItems } from '@rudi/shared/search';

interface ProfileTabProps {
  viewerSlug?: string | null;
  viewerLabel?: string | null;
  ownEmail?: string | null;
  viewerLoading?: boolean;
  viewerError?: string | null;
  canEdit?: boolean;
  demoMode?: boolean;
  operatorMode?: boolean;
  canSubmitChanges?: boolean;
  suggestion?: PiSuggestionResponse | null;
  suggestionUpdating?: boolean;
  onSuggestionStatusChange?: (
    surface: PiSuggestionSurface,
    status: Extract<PiSuggestionSurfaceStatus, 'applied' | 'dismissed'>
  ) => Promise<unknown> | unknown;
  onSuggestionRefresh?: () => Promise<unknown> | unknown;
}

export function ProfileTab({
  viewerSlug,
  viewerLabel,
  ownEmail,
  viewerLoading = false,
  viewerError = null,
  canEdit = false,
  demoMode = false,
  operatorMode = false,
  canSubmitChanges = !demoMode,
  suggestion = null,
  suggestionUpdating = false,
  onSuggestionStatusChange,
  onSuggestionRefresh,
}: ProfileTabProps) {
  const { t } = useClinicalEditors();
  if (!ownEmail && !viewerSlug) {
    return (
      <div
        className="p-8 text-center text-muted-foreground text-sm"
        data-onboarding-id="clinical-trials-profile"
        data-ai-id="clinical-trials.profile"
        data-ai-label="PI Profile"
        data-ai-type="section"
        data-ai-capabilities="highlight,waitForClick"
      >
        {t('clinicalTrials.ui.pleaseSignInToEditYourProfile')}
      </div>
    );
  }

  if (viewerLoading) {
    return (
      <div
        className="p-8 text-center text-muted-foreground text-sm"
        data-onboarding-id="clinical-trials-profile"
        data-ai-id="clinical-trials.profile"
        data-ai-label="PI Profile"
        data-ai-type="section"
        data-ai-capabilities="highlight,waitForClick"
      >
        {t('clinicalTrials.ui.searchingForProfile')}
      </div>
    );
  }

  if (viewerError) {
    return (
      <div
        className="p-8 text-center text-destructive text-sm"
        data-onboarding-id="clinical-trials-profile"
        data-ai-id="clinical-trials.profile"
        data-ai-label="PI Profile"
        data-ai-type="section"
        data-ai-capabilities="highlight,waitForClick"
      >
        {t('clinicalTrials.ui.couldNotFindTheProfile')} {viewerError}
      </div>
    );
  }

  if (!viewerSlug) {
    return (
      <div
        className="p-8 max-w-xl mx-auto text-sm text-muted-foreground space-y-2"
        data-onboarding-id="clinical-trials-profile"
        data-ai-id="clinical-trials.profile"
        data-ai-label="PI Profile"
        data-ai-type="section"
        data-ai-capabilities="highlight,waitForClick"
      >
        <div className="text-foreground font-medium">{t('clinicalTrials.ui.noLinkedProfile')}</div>
        <p>
          {t('clinicalTrials.ui.yourRudiAccount')}
          {ownEmail}
          {t('clinicalTrials.ui.hasNoLinkedProfileOnStudienNetzwerkDeYetf0d3e4')}
        </p>
      </div>
    );
  }

  return (
    <LinkedProfile
      slug={viewerSlug}
      label={viewerLabel}
      canEdit={canEdit}
      demoMode={demoMode}
      operatorMode={operatorMode}
      canSubmitChanges={canSubmitChanges}
      suggestion={suggestion}
      suggestionUpdating={suggestionUpdating}
      onSuggestionStatusChange={onSuggestionStatusChange}
      onSuggestionRefresh={onSuggestionRefresh}
    />
  );
}

interface DraftState {
  bio: string;
  groupName: string;
  groupDescription: string;
  photoUrl: string | null;
  photoObjectKey: string | null;
  phone: string;
  additionalPhones: string[];
  clinicRefs: ClinicRefDto[];
}

const EMPTY_DRAFT: DraftState = {
  bio: '',
  groupName: '',
  groupDescription: '',
  photoUrl: null,
  photoObjectKey: null,
  phone: '',
  additionalPhones: [],
  clinicRefs: [],
};

function toProfileDraftPreview(slug: string, draft: DraftState): ProfileDraftPreview {
  return {
    slug,
    bio: draft.bio || null,
    groupName: draft.groupName || null,
    groupDescription: draft.groupDescription || null,
    photoUrl: draft.photoUrl,
    phone: draft.phone || null,
    additionalPhones: draft.additionalPhones,
    clinicRefs: draft.clinicRefs,
  };
}

function createProfilePreviewMessage(slug: string, draft: ProfileDraftPreview) {
  const affiliations =
    draft.clinicRefs?.map(ref => ({
      clinic_id: ref.clinicId,
      clinic_slug: ref.slug,
      clinic_name: ref.name,
      role: null,
      clinic_type: null,
      is_primary: false,
    })) ?? null;
  const payload = {
    bio: draft.bio,
    groupName: draft.groupName,
    group_name: draft.groupName,
    groupDescription: draft.groupDescription,
    group_description: draft.groupDescription,
    photoUrl: draft.photoUrl,
    photo_url: draft.photoUrl,
    phone: draft.phone ?? null,
    additionalPhones: draft.additionalPhones ?? null,
    additional_phones: draft.additionalPhones ?? null,
    clinicRefs: draft.clinicRefs ?? null,
    clinic_refs: draft.clinicRefs ?? null,
    affiliations,
  };

  return {
    type: 'rudi:profile-preview' as const,
    version: 1,
    slug,
    payload,
    // Compatibility for iframe implementations that read the fields directly.
    ...payload,
  };
}

function createSluglessProfilePreviewMessage(draft: ProfileDraftPreview) {
  const message: Partial<ReturnType<typeof createProfilePreviewMessage>> = {
    ...createProfilePreviewMessage(draft.slug, draft),
  };
  delete message.slug;
  return message;
}

function deriveBaseline(
  investigator: PublicInvestigatorDetail | null,
  latest: ProfileEditRequest | null
): DraftState {
  const upstreamClinics: ClinicRefDto[] = investigator
    ? investigator.affiliations.map(a => ({
        clinicId: a.clinic_id,
        slug: a.clinic_slug,
        name: a.clinic_name,
      }))
    : [];
  return {
    bio: latest?.bio ?? investigator?.bio ?? '',
    groupName: latest?.groupName ?? investigator?.group_name ?? '',
    groupDescription: latest?.groupDescription ?? investigator?.group_description ?? '',
    photoUrl: latest?.photoUrl ?? investigator?.photo_url ?? null,
    photoObjectKey: latest?.photoObjectKey ?? null,
    phone: latest?.phone ?? investigator?.phone ?? '',
    additionalPhones: latest?.additionalPhones ?? investigator?.additional_phones ?? [],
    clinicRefs: latest?.clinicRefs ?? upstreamClinics,
  };
}

function arraysEqual<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!eq(a[i], b[i])) return false;
  }
  return true;
}

function clinicRefsEqual(a: ClinicRefDto[], b: ClinicRefDto[]): boolean {
  return arraysEqual(a, b, (x, y) => x.clinicId === y.clinicId);
}

function draftEqualsBaseline(draft: DraftState, baseline: DraftState): boolean {
  return (
    draft.bio === baseline.bio &&
    draft.groupName === baseline.groupName &&
    draft.groupDescription === baseline.groupDescription &&
    draft.photoUrl === baseline.photoUrl &&
    draft.photoObjectKey === baseline.photoObjectKey &&
    draft.phone === baseline.phone &&
    arraysEqual(draft.additionalPhones, baseline.additionalPhones, (x, y) => x === y) &&
    clinicRefsEqual(draft.clinicRefs, baseline.clinicRefs)
  );
}

type EditSection = 'bio' | 'group' | 'photo' | 'phones' | 'clinics' | 'all';

function LinkedProfile({
  slug,
  label,
  canEdit,
  demoMode,
  operatorMode,
  canSubmitChanges,
  suggestion,
  suggestionUpdating,
  onSuggestionStatusChange,
  onSuggestionRefresh,
}: {
  slug: string;
  label?: string | null;
  canEdit: boolean;
  demoMode: boolean;
  operatorMode: boolean;
  canSubmitChanges: boolean;
  suggestion?: PiSuggestionResponse | null;
  suggestionUpdating?: boolean;
  onSuggestionStatusChange?: (
    surface: PiSuggestionSurface,
    status: Extract<PiSuggestionSurfaceStatus, 'applied' | 'dismissed'>
  ) => Promise<unknown> | unknown;
  onSuggestionRefresh?: () => Promise<unknown> | unknown;
}) {
  const { t, profileOrigin: STUDIEN_NETZWERK_ORIGIN } = useClinicalEditors();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeTargetOrigin, setIframeTargetOrigin] = useState<string | null>(null);
  const [previewDraft, setPreviewDraft] = useState<ProfileDraftPreview | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState<EditSection>('all');

  // Upstream profile (provides current phones/clinics for seeding + diffing).
  const {
    investigator,
    loading: investigatorLoading,
    error: investigatorError,
  } = usePublicInvestigator(slug);
  // Latest submitted edit request (still pending or already applied).
  const { latest, loading, error, saving, save, uploadPhoto } = useProfileEditRequest(
    slug,
    canSubmitChanges && !demoMode
  );

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [baseline, setBaseline] = useState<DraftState>(EMPTY_DRAFT);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [acceptedSuggestionId, setAcceptedSuggestionId] = useState<string | null>(null);

  // Seed draft + baseline once both upstream investigator and latest request are loaded.
  const seededRef = useRef(false);
  useEffect(() => {
    seededRef.current = false;
    setDraft(EMPTY_DRAFT);
    setBaseline(EMPTY_DRAFT);
    setSubmittedAt(null);
    setJustSubmitted(false);
    setSubmitErr(null);
    setAcceptedSuggestionId(null);
    clearProfileDraft();
  }, [slug]);

  useEffect(() => {
    if (loading || investigatorLoading || seededRef.current) return;
    if (investigator && investigator.slug !== slug) return;
    if (latest && latest.slug !== slug) return;
    // We can seed without investigator (it might 404 if upstream doesn't know
    // the slug); latest alone is enough for bio/group/photo.
    const next = deriveBaseline(investigator, latest);
    setDraft(next);
    setBaseline(next);
    setSubmittedAt(latest?.submittedAt ?? null);
    seededRef.current = true;
  }, [loading, investigatorLoading, latest, investigator, slug]);

  const iframeSrc = useMemo(
    () => `${STUDIEN_NETZWERK_ORIGIN}/pruefaerzte/${encodeURIComponent(slug)}?embed=rudi`,
    [slug, STUDIEN_NETZWERK_ORIGIN]
  );

  const iframeOrigin = useMemo(() => {
    try {
      return new URL(STUDIEN_NETZWERK_ORIGIN).origin;
    } catch {
      return STUDIEN_NETZWERK_ORIGIN;
    }
  }, [STUDIEN_NETZWERK_ORIGIN]);

  useEffect(() => {
    setIframeReady(false);
    setIframeTargetOrigin(null);
    setPreviewDraft(null);
  }, [slug, iframeSrc, iframeOrigin]);

  useEffect(() => {
    return subscribeProfileDraft(next => {
      setPreviewDraft(next?.slug === slug ? next : null);
    });
  }, [slug]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as { type?: string; slug?: string } | null;
      if (data?.type === 'rudi:profile-ready' && (!data.slug || data.slug === slug)) {
        setIframeReady(true);
        setIframeTargetOrigin(event.origin);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [slug]);

  const postProfilePreview = useCallback(
    (
      draftToSend: ProfileDraftPreview | null = previewDraft,
      options: { repeat?: boolean } = {}
    ) => {
      if (!draftToSend || draftToSend.slug !== slug) return;
      const targetWindow = iframeRef.current?.contentWindow;
      if (!targetWindow) return;

      const messages = [
        createProfilePreviewMessage(slug, draftToSend),
        createSluglessProfilePreviewMessage(draftToSend),
      ];
      const targetOrigins = [iframeTargetOrigin ?? iframeOrigin];
      if (!iframeTargetOrigin) targetOrigins.push('*');

      const send = () => {
        for (const message of messages) {
          for (const targetOrigin of targetOrigins) {
            targetWindow.postMessage(message, targetOrigin);
          }
        }
      };

      send();

      if (options.repeat) {
        window.setTimeout(send, 150);
        window.setTimeout(send, 600);
        window.setTimeout(send, 1500);
      }
    },
    [iframeOrigin, iframeTargetOrigin, previewDraft, slug]
  );

  useEffect(() => {
    postProfilePreview(previewDraft, { repeat: iframeReady });
  }, [iframeReady, postProfilePreview, previewDraft]);

  // Publish draft to bus on every change so the iframe can live-preview.
  useEffect(() => {
    if (!seededRef.current) return;
    publishProfileDraft(toProfileDraftPreview(slug, draft));
  }, [draft, slug]);

  const openEdit = useCallback(
    (section: EditSection) => {
      if (loading || investigatorLoading) return;
      setEditSection(section);
      setEditOpen(true);
      setJustSubmitted(false);
    },
    [loading, investigatorLoading]
  );

  const closeEdit = useCallback(() => {
    const nextPreview = toProfileDraftPreview(slug, draft);
    publishProfileDraft(nextPreview);
    postProfilePreview(nextPreview, { repeat: true });
    setEditOpen(false);
  }, [draft, postProfilePreview, slug]);

  const handleEditOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setEditOpen(true);
        return;
      }
      closeEdit();
    },
    [closeEdit]
  );

  const handlePhoto = useCallback(
    async (file: File) => {
      setUploadingPhoto(true);
      try {
        if (demoMode || !canSubmitChanges) {
          const url = await readFileAsDataUrl(file);
          setDraft(d => ({ ...d, photoUrl: url, photoObjectKey: null }));
          return;
        }
        const { objectKey, url } = await uploadPhoto(file);
        setDraft(d => ({ ...d, photoUrl: url, photoObjectKey: objectKey }));
      } finally {
        setUploadingPhoto(false);
      }
    },
    [canSubmitChanges, demoMode, uploadPhoto]
  );

  const handlePasteUrl = useCallback((url: string) => {
    setDraft(d => ({
      ...d,
      photoUrl: url.trim() || null,
      photoObjectKey: null,
    }));
  }, []);

  const hasUnsubmittedChanges = useMemo(
    () => seededRef.current && !draftEqualsBaseline(draft, baseline),
    [draft, baseline]
  );

  const hasProfileSuggestion =
    canEdit &&
    !!suggestion &&
    suggestion.piSlug === slug &&
    (suggestion.profileStatus === 'pending' || suggestion.profileStatus === 'applied');

  const applyProfileSuggestion = useCallback(async () => {
    if (!suggestion) return;
    const suggested = suggestion.profile;
    setDraft(current => ({
      ...current,
      bio: suggested.bio ?? current.bio,
      groupName: suggested.groupName ?? current.groupName,
      groupDescription: suggested.groupDescription ?? current.groupDescription,
      photoUrl: suggested.photoUrl ?? current.photoUrl,
      photoObjectKey: suggested.photoUrl ? null : current.photoObjectKey,
      phone: suggested.phone ?? current.phone,
      additionalPhones:
        suggested.additionalPhones.length > 0
          ? suggested.additionalPhones
          : current.additionalPhones,
      clinicRefs: suggested.clinicRefs.length > 0 ? suggested.clinicRefs : current.clinicRefs,
    }));
    setAcceptedSuggestionId(suggestion.id);
    setJustSubmitted(false);
    setEditSection('all');
    setEditOpen(true);
    if (canSubmitChanges && !demoMode) {
      await onSuggestionStatusChange?.('profile', 'applied');
    }
  }, [canSubmitChanges, demoMode, onSuggestionStatusChange, suggestion]);

  const dismissProfileSuggestion = useCallback(async () => {
    if (!suggestion) return;
    setAcceptedSuggestionId(null);
    await onSuggestionStatusChange?.('profile', 'dismissed');
  }, [onSuggestionStatusChange, suggestion]);

  const handleSubmit = useCallback(async () => {
    setSubmitErr(null);
    if (!canSubmitChanges || demoMode) {
      setSubmitErr('Diese Profilansicht kann keine Änderungen einreichen.');
      return;
    }
    const hasUpload = !!draft.photoObjectKey;
    const sourceSuggestionId =
      acceptedSuggestionId ??
      (suggestion?.id && suggestion.profileStatus === 'applied' ? suggestion.id : undefined);
    const payload: ProfileEditRequestPayload = {
      slug,
      bio: draft.bio.trim() || null,
      groupName: draft.groupName.trim() || null,
      groupDescription: draft.groupDescription.trim() || null,
      photoObjectKey: hasUpload ? draft.photoObjectKey : null,
      photoUrl: hasUpload ? null : draft.photoUrl,
      phone: draft.phone.trim() || null,
      additionalPhones:
        draft.additionalPhones.length === baseline.additionalPhones.length &&
        arraysEqual(draft.additionalPhones, baseline.additionalPhones, (a, b) => a === b)
          ? null
          : draft.additionalPhones,
      clinicRefs: clinicRefsEqual(draft.clinicRefs, baseline.clinicRefs) ? null : draft.clinicRefs,
      sourceSuggestionId,
    };
    try {
      const saved = await save(payload);
      try {
        await onSuggestionRefresh?.();
      } catch {
        // Submission already succeeded; stale suggestion state can recover on reload.
      }
      setSubmittedAt(saved.submittedAt);
      setJustSubmitted(true);
      // After successful submit, the just-submitted draft becomes the new baseline
      // so the toolbar submit button hides until the user makes another change.
      setBaseline(draft);
      setAcceptedSuggestionId(null);
      setEditOpen(false);
    } catch (err) {
      setSubmitErr(err instanceof Error ? err.message : String(err));
    }
  }, [
    acceptedSuggestionId,
    baseline,
    canSubmitChanges,
    demoMode,
    draft,
    onSuggestionRefresh,
    save,
    slug,
    suggestion,
  ]);

  // Clean up draft bus on unmount
  useEffect(() => () => clearProfileDraft(), []);

  const sectionTitle =
    editSection === 'bio'
      ? t('clinicalTrials.ui.editBiography')
      : editSection === 'group'
        ? t('clinicalTrials.ui.editWorkingGroup')
        : editSection === 'photo'
          ? t('clinicalTrials.ui.changeProfilePicture')
          : editSection === 'phones'
            ? t('clinicalTrials.ui.editPhoneNumbers')
            : editSection === 'clinics'
              ? t('clinicalTrials.ui.editPractices')
              : t('clinicalTrials.ui.editProfile');

  const submittedLabel = demoMode
    ? t('clinicalTrials.ui.localDemoPreview')
    : submittedAt
      ? t('clinicalTrials.ui.lastSubmissionValue1Value2', {
          value1: formatTimestamp(submittedAt),
          value2: latest?.status === 'applied' ? 'übernommen' : 'ausstehend',
        })
      : t('clinicalTrials.ui.noSubmissionYet');

  const editHeader = (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-3xl bg-primary/10 text-primary">
          <Pencil className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold leading-tight text-foreground">{sectionTitle}</div>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            {operatorMode
              ? t('clinicalTrials.ui.editThePublicProfileDetailsValue1ThePreviewUpdates', {
                  value1: label ? ` von ${label}` : '',
                })
              : t('clinicalTrials.ui.editYourPublicProfileDetailsThePreviewUpdatesDirectly')}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill icon={Clock3}>{submittedLabel}</StatusPill>
        {hasUnsubmittedChanges ? (
          <StatusPill icon={Eye} tone="primary">
            {t('clinicalTrials.ui.previewWithNewChanges')}
          </StatusPill>
        ) : (
          <StatusPill icon={CheckCircle2}>{t('clinicalTrials.ui.noPendingChanges')}</StatusPill>
        )}
      </div>
    </div>
  );

  const editFooter = (
    <div className="space-y-3">
      {submitErr && <InlineNotice tone="error">{submitErr}</InlineNotice>}
      {justSubmitted && (
        <InlineNotice tone="success">
          {t('clinicalTrials.ui.yourChangesHaveBeenSubmittedAndWillBeReviewed')}
        </InlineNotice>
      )}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-muted-foreground sm:max-w-md">
          {demoMode
            ? t('clinicalTrials.ui.theseChangesApplyOnlyToTheCurrentDemoPreview')
            : t('clinicalTrials.ui.changesArePublishedOnStudienNetzwerkDeAfterA')}
        </p>
        <div className="flex gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={closeEdit}
            disabled={saving || uploadingPhoto}
            className="flex-1 sm:flex-none"
          >
            {t('clinicalTrials.ui.closePreview')}
          </Button>
          {canSubmitChanges && !demoMode && (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!hasUnsubmittedChanges || saving || loading || uploadingPhoto}
              className="flex-1 sm:flex-none"
              data-onboarding-id="clinical-trials-profile-submit"
              data-ai-id="clinical-trials.profile-submit"
              data-ai-label="Submit Profile Changes"
              data-ai-type="button"
              data-ai-capabilities="highlight,waitForClick,submitsData,mutatesData,sensitive"
            >
              <Send className="mr-2 h-4 w-4" />
              {saving ? t('clinicalTrials.ui.submitting') : t('clinicalTrials.ui.submit')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="h-full min-h-0 flex flex-col bg-muted/20"
      data-onboarding-id="clinical-trials-profile"
      data-ai-id="clinical-trials.profile"
      data-ai-label="PI Profile"
      data-ai-type="section"
      data-ai-capabilities="highlight,waitForClick"
    >
      {/* Edit toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background">
        <div className="text-sm font-medium text-foreground">
          {demoMode
            ? t('clinicalTrials.ui.demoPreviewValue1', { value1: label ? `: ${label}` : '' })
            : operatorMode
              ? t('clinicalTrials.ui.editProfileValue1', {
                  value1: label ? `: ${label}` : '',
                })
              : canEdit
                ? t('clinicalTrials.ui.yourProfileOnStudienNetzwerkDe')
                : t('clinicalTrials.ui.profileValue1', {
                    value1: label ? ` von ${label}` : '',
                  })}
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEdit('all')}
              disabled={loading || investigatorLoading}
              data-onboarding-id="clinical-trials-profile-photo-edit"
              data-ai-id="clinical-trials.profile-photo-edit"
              data-ai-label="Edit Profile"
              data-ai-type="button"
              data-ai-capabilities="highlight,waitForClick,externalEffect,sensitive"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              {t('clinicalTrials.ui.editProfile')}
            </Button>
            {hasUnsubmittedChanges && canSubmitChanges && !demoMode && (
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={saving || loading || uploadingPhoto}
                data-onboarding-id="clinical-trials-profile-submit-toolbar"
                data-ai-id="clinical-trials.profile-submit"
                data-ai-label="Submit Profile Changes"
                data-ai-type="button"
                data-ai-capabilities="highlight,waitForClick,submitsData,mutatesData,sensitive"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {saving ? t('clinicalTrials.ui.submitting') : t('clinicalTrials.ui.submitChanges')}
              </Button>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            {t('clinicalTrials.ui.selectAViewInTheSidebar')}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {canEdit && investigatorError && (
          <div className="border-b bg-destructive/5 px-4 py-2 text-xs text-destructive">
            {t('clinicalTrials.ui.couldNotLoadProfileDataForTheForm')} {investigatorError}
          </div>
        )}
        {hasProfileSuggestion && (
          <div className="border-b bg-primary/5 px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-3xl bg-primary/10 text-primary">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {t('clinicalTrials.ui.profileSuggestionAvailable')}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {t('clinicalTrials.ui.youCanApplyAPreparedSuggestionToTheDraft')}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {canSubmitChanges && !demoMode && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void dismissProfileSuggestion()}
                    disabled={suggestionUpdating}
                  >
                    {t('clinicalTrials.ui.reject')}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void applyProfileSuggestion()}
                  disabled={suggestionUpdating}
                >
                  {demoMode ? t('clinicalTrials.ui.applyToPreview') : t('clinicalTrials.ui.apply')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {demoMode || operatorMode ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            {investigatorLoading || investigator?.slug !== slug ? (
              <p className="text-sm text-muted-foreground">
                {investigatorError ?? t('clinicalTrials.ui.loadingProfileData')}
              </p>
            ) : (
              <article className="mx-auto max-w-3xl space-y-6 rounded-3xl border bg-card p-6">
                <div className="flex items-start gap-5">
                  {draft.photoUrl && (
                    <img
                      src={draft.photoUrl}
                      alt={investigator.full_name}
                      className="h-28 w-28 rounded-3xl object-cover"
                    />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">{investigator.title}</p>
                    <h1 className="text-2xl font-semibold">{investigator.full_name}</h1>
                    <p className="mt-1 text-muted-foreground">{investigator.specialty}</p>
                  </div>
                </div>
                {draft.bio && <p className="whitespace-pre-wrap">{draft.bio}</p>}
                {(draft.groupName || draft.groupDescription) && (
                  <section className="space-y-2">
                    <h2 className="text-lg font-semibold">
                      {draft.groupName || t('clinicalTrials.ui.workingGroup')}
                    </h2>
                    <p className="whitespace-pre-wrap text-muted-foreground">
                      {draft.groupDescription}
                    </p>
                  </section>
                )}
                {draft.clinicRefs.length > 0 && (
                  <section className="space-y-2">
                    <h2 className="text-lg font-semibold">
                      {t('clinicalTrials.ui.practicesAndClinics')}
                    </h2>
                    <ul className="space-y-1 text-muted-foreground">
                      {draft.clinicRefs.map(clinic => (
                        <li key={clinic.clinicId}>{clinic.name}</li>
                      ))}
                    </ul>
                  </section>
                )}
                {(draft.phone || draft.additionalPhones.length > 0) && (
                  <section className="space-y-2">
                    <h2 className="text-lg font-semibold">{t('clinicalTrials.ui.phoneNumbers')}</h2>
                    <p className="text-muted-foreground">
                      {[draft.phone, ...draft.additionalPhones].filter(Boolean).join(' · ')}
                    </p>
                  </section>
                )}
              </article>
            )}
          </div>
        ) : (
          <iframe
            key={iframeSrc}
            ref={iframeRef}
            title={t('clinicalTrials.ui.studyNetworkProfileValue1', {
              value1: label ?? slug,
            })}
            src={iframeSrc}
            className="flex-1 min-h-0 w-full border-0 bg-white"
            onLoad={() => setIframeReady(true)}
          />
        )}
      </div>

      {/* Edit modal */}
      <AdaptiveDialog
        open={canEdit && editOpen}
        onOpenChange={handleEditOpenChange}
        title={sectionTitle}
        description={t('clinicalTrials.ui.editThePublicProfileDetailsAndCheckThePreview')}
        headerContent={editHeader}
        maxWidth="2xl"
        snapPoints={[60, 98]}
        defaultSnapIndex={1}
        className="bg-muted/20"
        footer={editFooter}
      >
        {loading ? (
          <div className="rounded-3xl border border-border bg-card p-4 text-sm text-muted-foreground">
            {t('clinicalTrials.ui.loadingProfileData')}
          </div>
        ) : (
          <div className="space-y-4">
            {error && <InlineNotice tone="error">{error}</InlineNotice>}

            <InlineNotice>
              {demoMode
                ? t('clinicalTrials.ui.demoPreviewChangesStayInThisBrowserAndAre')
                : operatorMode
                  ? t('clinicalTrials.ui.changesAreSubmittedForReviewUnderYourAccountFor', {
                      value1: label ? ` von ${label}` : '',
                    })
                  : t('clinicalTrials.ui.yourChangesAreSubmittedForReviewUntilApprovedThey')}
            </InlineNotice>

            {(editSection === 'bio' || editSection === 'all') && (
              <EditPanel
                icon={FileText}
                title={t('clinicalTrials.ui.biography')}
                description={t(
                  'clinicalTrials.ui.shortProfileResearchInterestsAndClinicalExperience'
                )}
              >
                <div className="space-y-2">
                  <Label htmlFor="pf-bio">Bio</Label>
                  <Textarea
                    id="pf-bio"
                    value={draft.bio}
                    onChange={e => setDraft(d => ({ ...d, bio: e.target.value }))}
                    rows={5}
                    placeholder={t('clinicalTrials.ui.aboutMe')}
                    className="min-h-[132px] resize-none bg-background"
                  />
                </div>
              </EditPanel>
            )}

            {(editSection === 'group' || editSection === 'all') && (
              <EditPanel
                icon={Building2}
                title={t('clinicalTrials.ui.workingGroup')}
                description={t('clinicalTrials.ui.nameAndDescriptionOfTheAssociatedWorkingGroup')}
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="pf-group-name">{t('clinicalTrials.ui.workingGroup')}</Label>
                    <Input
                      id="pf-group-name"
                      value={draft.groupName}
                      onChange={e => setDraft(d => ({ ...d, groupName: e.target.value }))}
                      placeholder={t('clinicalTrials.ui.eGOncologicalDermatologyGroup')}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="pf-group-desc">
                      {t('clinicalTrials.ui.workingGroupDescription')}
                    </Label>
                    <Textarea
                      id="pf-group-desc"
                      value={draft.groupDescription}
                      onChange={e => setDraft(d => ({ ...d, groupDescription: e.target.value }))}
                      rows={4}
                      placeholder={t('clinicalTrials.ui.focusAreasResearchQuestions')}
                      className="min-h-[112px] resize-none bg-background"
                    />
                  </div>
                </div>
              </EditPanel>
            )}

            {(editSection === 'photo' || editSection === 'all') && (
              <EditPanel
                icon={ImageIcon}
                title={t('clinicalTrials.ui.profileImage')}
                description={t('clinicalTrials.ui.updateTheImageOnThePublicProfile')}
                onboardingId="clinical-trials-profile-photo-section"
              >
                <div className="space-y-3">
                  <ImageUploadTile
                    currentUrl={draft.photoUrl}
                    uploading={uploadingPhoto}
                    onFile={handlePhoto}
                    onClear={() => setDraft(d => ({ ...d, photoUrl: null, photoObjectKey: null }))}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="pf-photo-url">{t('clinicalTrials.ui.orPasteAnImageUrl')}</Label>
                    <Input
                      id="pf-photo-url"
                      value={draft.photoObjectKey ? '' : (draft.photoUrl ?? '')}
                      onChange={e => handlePasteUrl(e.target.value)}
                      placeholder="https://…"
                      disabled={!!draft.photoObjectKey}
                      className="bg-background"
                    />
                    {draft.photoObjectKey && (
                      <div className="text-[11px] text-muted-foreground">
                        {t('clinicalTrials.ui.urlDisabledRemoveTheUploadedImageFirst')}
                      </div>
                    )}
                  </div>
                </div>
              </EditPanel>
            )}

            {(editSection === 'phones' || editSection === 'all') && (
              <EditPanel
                icon={Phone}
                title={t('clinicalTrials.ui.phoneNumbers')}
                description={t('clinicalTrials.ui.primaryAndAdditionalPhoneNumbersForEnquiries')}
              >
                <PhonesSection
                  phone={draft.phone}
                  additional={draft.additionalPhones}
                  onPhoneChange={v => setDraft(d => ({ ...d, phone: v }))}
                  onAdditionalChange={next => setDraft(d => ({ ...d, additionalPhones: next }))}
                />
              </EditPanel>
            )}

            {(editSection === 'clinics' || editSection === 'all') && (
              <EditPanel
                icon={Building2}
                title={t('clinicalTrials.ui.practicesAndClinics')}
                description={t('clinicalTrials.ui.existingPublicLocationsShownOnTheProfile')}
                onboardingId="clinical-trials-profile-clinics"
              >
                <ClinicsSection
                  selected={draft.clinicRefs}
                  onChange={next => setDraft(d => ({ ...d, clinicRefs: next }))}
                />
              </EditPanel>
            )}
          </div>
        )}
      </AdaptiveDialog>
    </div>
  );
}

function StatusPill({
  icon: Icon,
  children,
  tone = 'muted',
}: {
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  tone?: 'muted' | 'primary';
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-3xl border px-2.5 py-1 text-xs ${
        tone === 'primary'
          ? 'border-primary/20 bg-primary/10 text-primary'
          : 'border-border bg-muted/50 text-muted-foreground'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

function InlineNotice({
  children,
  tone = 'info',
}: {
  children: ReactNode;
  tone?: 'info' | 'success' | 'error';
}) {
  const toneClass =
    tone === 'success'
      ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : tone === 'error'
        ? 'border-destructive/25 bg-destructive/10 text-destructive'
        : 'border-primary/20 bg-primary/5 text-foreground';
  const iconClass =
    tone === 'success'
      ? 'text-emerald-600 dark:text-emerald-300'
      : tone === 'error'
        ? 'text-destructive'
        : 'text-primary';
  const Icon = tone === 'success' ? CheckCircle2 : Info;

  return (
    <div className={`flex gap-2 rounded-3xl border p-3 ${toneClass}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`} />
      <p className="text-xs leading-relaxed">{children}</p>
    </div>
  );
}

function EditPanel({
  icon: Icon,
  title,
  description,
  children,
  onboardingId,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children: ReactNode;
  onboardingId?: string;
}) {
  return (
    <section
      className="rounded-3xl border border-border bg-card p-4 shadow-sm"
      data-onboarding-id={onboardingId}
    >
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-3xl bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function PhonesSection({
  phone,
  additional,
  onPhoneChange,
  onAdditionalChange,
}: {
  phone: string;
  additional: string[];
  onPhoneChange: (v: string) => void;
  onAdditionalChange: (next: string[]) => void;
}) {
  const { t } = useClinicalEditors();
  const [newPhone, setNewPhone] = useState('');

  const addPhone = () => {
    const v = newPhone.trim();
    if (!v) return;
    if (additional.includes(v) || v === phone.trim()) {
      setNewPhone('');
      return;
    }
    onAdditionalChange([...additional, v]);
    setNewPhone('');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pf-phone" className="text-xs font-medium text-muted-foreground">
          {t('clinicalTrials.ui.primaryNumber')}
        </Label>
        <Input
          id="pf-phone"
          value={phone}
          onChange={e => onPhoneChange(e.target.value)}
          placeholder="z. B. +49 30 12345 678"
          className="bg-background"
        />
      </div>

      {additional.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            {t('clinicalTrials.ui.additionalNumbers')}
          </div>
          <ul className="space-y-2">
            {additional.map((num, idx) => (
              <li
                key={`${num}-${idx}`}
                className="flex items-center gap-2 rounded-3xl border border-border bg-background px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">{num}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => onAdditionalChange(additional.filter((_, i) => i !== idx))}
                  aria-label={t('clinicalTrials.ui.removeNumberValue1', { value1: num })}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="pf-phone-new" className="text-xs font-medium text-muted-foreground">
          {t('clinicalTrials.ui.addAnotherNumber')}
        </Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id="pf-phone-new"
            value={newPhone}
            onChange={e => setNewPhone(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addPhone();
              }
            }}
            placeholder="z. B. +49 30 12345 679"
            className="bg-background"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPhone}
            disabled={!newPhone.trim()}
            className="sm:w-auto"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {t('clinicalTrials.ui.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClinicsSection({
  selected,
  onChange,
}: {
  selected: ClinicRefDto[];
  onChange: (next: ClinicRefDto[]) => void;
}) {
  const { t, listPublicClinics } = useClinicalEditors();
  const [query, setQuery] = useState('');
  const [allClinics, setAllClinics] = useState<PublicClinicSummary[] | null>(null);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingClinics(true);
    setLoadErr(null);
    listPublicClinics()
      .then(rows => {
        if (!cancelled) setAllClinics(rows);
      })
      .catch(err => {
        if (!cancelled) setLoadErr(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingClinics(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listPublicClinics]);

  const selectedIds = useMemo(() => new Set(selected.map(c => c.clinicId)), [selected]);

  const matches = useMemo(() => {
    if (!allClinics) return [];
    if (!query.trim()) return [];
    return rankSearchItems(
      allClinics.filter(clinic => !selectedIds.has(clinic.id)),
      query,
      clinic => [
        { value: clinic.name, priority: 2 },
        { value: clinic.slug, priority: 1 },
        { value: clinic.id, kind: 'identifier' },
      ]
    ).slice(0, 8);
  }, [allClinics, query, selectedIds]);

  const addClinic = (clinic: PublicClinicSummary) => {
    onChange([...selected, { clinicId: clinic.id, slug: clinic.slug, name: clinic.name }]);
    setQuery('');
  };

  return (
    <div className="space-y-4">
      {selected.length > 0 ? (
        <ul className="space-y-2">
          {selected.map(c => (
            <li
              key={c.clinicId}
              className="flex items-center gap-3 rounded-3xl border border-border bg-background px-3 py-2"
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground block truncate">{c.name}</span>
                <span className="text-[11px] text-muted-foreground block truncate">{c.slug}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => onChange(selected.filter(x => x.clinicId !== c.clinicId))}
                aria-label={t('clinicalTrials.ui.removeValue1', { value1: c.name })}
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-3xl border border-dashed border-border bg-background px-3 py-3 text-xs text-muted-foreground">
          {t('clinicalTrials.ui.noClinicsOnFileYet')}
        </p>
      )}

      <div className="space-y-2">
        <Label htmlFor="pf-clinic-search" className="text-xs font-medium text-muted-foreground">
          {t('clinicalTrials.ui.findAndAddAClinic')}
        </Label>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            id="pf-clinic-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder={
              loadingClinics
                ? t('clinicalTrials.ui.loadingClinics')
                : t('clinicalTrials.ui.enterAName')
            }
            className="bg-background pl-8"
            disabled={loadingClinics}
          />
          {focused && query.trim() && matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-3xl border border-border bg-popover shadow-md max-h-60 overflow-y-auto">
              {matches.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => addClinic(c)}
                  className="w-full flex items-start gap-2 px-2 py-2 text-left hover:bg-muted text-sm border-b border-border last:border-b-0"
                >
                  <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-foreground truncate">{c.name}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">
                      {[c.city, c.country].filter(Boolean).join(', ') || c.slug}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {focused && query.trim() && !loadingClinics && matches.length === 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-3xl border border-border bg-popover shadow-md px-3 py-2 text-xs text-muted-foreground">
              {t('clinicalTrials.ui.noClinicFound')}
            </div>
          )}
        </div>
        {loadErr && (
          <p className="text-[11px] text-destructive">
            {t('clinicalTrials.ui.clinicList')} {loadErr}
          </p>
        )}
      </div>
    </div>
  );
}

function ImageUploadTile({
  currentUrl,
  uploading,
  onFile,
  onClear,
}: {
  currentUrl: string | null;
  uploading: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}) {
  const { t } = useClinicalEditors();
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex flex-col gap-3 rounded-3xl border border-border bg-background p-3 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3 sm:flex-1">
        <div className="h-20 w-20 rounded-3xl border border-border bg-muted/60 flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt={t('clinicalTrials.ui.profileImage')}
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">
            {currentUrl
              ? t('clinicalTrials.ui.profileImageSelected')
              : t('clinicalTrials.ui.noProfileImageSelected')}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t('clinicalTrials.ui.useASquareImageForTheBestDisplay')}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-onboarding-id="clinical-trials-profile-photo-upload"
          data-ai-id="clinical-trials.profile-photo-upload"
          data-ai-label="Upload Profile Photo"
          data-ai-type="button"
          data-ai-capabilities="highlight,waitForClick,externalEffect,sensitive"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {uploading
            ? t('clinicalTrials.ui.loading')
            : currentUrl
              ? t('clinicalTrials.ui.replace')
              : t('clinicalTrials.ui.upload')}
        </Button>
        {currentUrl && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear} disabled={uploading}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            {t('clinicalTrials.ui.remove')}
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
